import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { FixingMode } from "@/data/fixingMode";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  DatePickerField,
  InputField,
  SelectField,
  SelectFieldWithSection,
  TSections,
  TextareaField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { FormActionsNew } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { useDynamicOptionsBuilder } from "@/features/FormCard/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useFeesCalculator } from "@/hooks/useFeesCalculator";
import { useEffect, useMemo, useRef } from "react";
import { DEFAULT_CATEGORY_OPTIONS, CategoryOption } from "../../a6/A6Transactions/config";
import { computeC3FixingMode } from "../C3TimeSeries/utils";
import { getC3FilteredCategoryOptions, isCashAssetClass } from "./utils";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  c3_portfolio_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().nullable().optional(),
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string(),
        currency: z.string(),
        reporting_currency: z.string(),
      }),
    ]),
  ),
  c3_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().nullable().optional(),
      z.object({
        text: z.string(),
        id: z.number(),
        asset_class_2: z.string(),
        asset_class_1: z.string(),
        currency: z.string(),
      }),
    ]),
  ),
  c3_portfolio_reporting_currency: z.string().nullable().optional(),
  c3_static_datum_asset_class_2: z.string().nullable().optional(),
  c3_static_datum_asset_class_1: z.string().nullable().optional(),
  c3_portfolio_account_type: z.string().nullable().optional(),
  c3_portfolio_currency: z.string().nullable().optional(),
  c3_static_datum_currency: z.string().nullable().optional(),
  description: z.string().optional().nullable(),
  quantity: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  fees: z.coerce.number().nullable().optional(),
  tax: z.coerce.number().nullable().optional(),
  custodian_broker_fee: z.coerce.number().nullable().optional(),
  broker_fee: z.coerce.number().nullable().optional(),
  other_fee: z.coerce.number().nullable().optional(),
  fee_currency: z.string().nullable().optional(),
  fee_type: z.string().nullable().optional(),
  category: z.string().optional().nullable(),
  transfer: z.string().optional().nullable(),
  c3_portfolio_id: z.number(),
  c3_static_datum_id: z.number(),
  time_serie_file_id: z.coerce.number().nullable().optional(),
  c3_time_serie_file_id: z.coerce.number().nullable().optional(),
});

type BaseForm = z.infer<typeof FormSchema>;

type TForm = Omit<BaseForm, "c3_portfolio_name" | "c3_static_datum_name"> & {
  c3_portfolio_name: Extract<BaseForm["c3_portfolio_name"], { id: number }>;
  c3_static_datum_name: Extract<
    BaseForm["c3_static_datum_name"],
    { id: number }
  >;
};

const client = Clients.C3;
const table_name = Tables.C3_TRANSACTIONS;

function deriveLocalCurrency(
  accountType?: string | null,
  portfolioCurrency?: string | null,
  assetCurrency?: string | null,
): string | null {
  if (accountType === "cash") return portfolioCurrency ?? null;
  if (accountType === "securities") return assetCurrency ?? null;
  return null;
}

function deriveSupportedAccountType(
  assetClass2: string | null | undefined,
  staticDatumName?: string,
): "cash" | "securities" | null {
  if (isCashAssetClass(assetClass2, staticDatumName)) return "cash";
  return null;
}

export function C3TransactionsNew() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const uiLanguage = i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en";

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    translations,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    fieldsNotApplyTranslation: ["transfer", "fee_type"],
    shouldUnregister: true,
    isNew: true,
  });

  const navigateToList = () => navigate(-1);
  const currencyOptions = useCurrencyOptions();

  const asset = form.watch("c3_static_datum_name") as TForm["c3_static_datum_name"];
  const portfolio = form.watch("c3_portfolio_name") as TForm["c3_portfolio_name"];

  const portfolioId = portfolio?.id;
  const portfolioType = portfolio?.account_type;
  const portfolioCurrency = portfolio?.currency;
  const reportingCurrency = portfolio?.reporting_currency;

  const assetCurrency = asset?.currency;
  const assetClass1 = asset?.asset_class_1 ?? null;
  const assetClass2 = asset?.asset_class_2 ?? null;
  const clientName = form.getValues("client")?.text ?? "";
  const bankName = form.getValues("bank_name")?.text ?? "";

  const isTypeCash = portfolioType === "cash";
  const isTypeSecurities = portfolioType === "securities";
  const isCashAsset = useMemo(
    () => isCashAssetClass(assetClass2, asset?.text),
    [assetClass2, asset?.text],
  );
  const marketValueLocalCcy = form.watch("market_value_local_ccy");

  const categoryOptions = useDynamicOptionsBuilder({
    translation: translations?.category,
    options: DEFAULT_CATEGORY_OPTIONS,
    sectionNameTranslationPrefix: "transactions.category_groups",
    adjustOptions: (options) =>
      getC3FilteredCategoryOptions(
        options as CategoryOption[],
        portfolioType,
        assetClass1,
        isCashAsset,
        marketValueLocalCcy ?? undefined,
      ),
    deps: [translations, portfolioType, assetClass1, isCashAsset, marketValueLocalCcy],
  });

  const isCategoriesEmpty = categoryOptions.options.length === 0;

  const localCurrency = useMemo(
    () =>
      deriveLocalCurrency(portfolioType ?? null, portfolioCurrency, assetCurrency),
    [portfolioType, portfolioCurrency, assetCurrency],
  );

  const supportedAccountType = useMemo(
    () => deriveSupportedAccountType(assetClass2, asset?.text),
    [assetClass2, asset?.text],
  );

  const fixingMode: FixingMode | null = useMemo(
    () => computeC3FixingMode(assetClass1 ?? undefined),
    [assetClass1],
  );

  const showQuantityField =
    isTypeSecurities && fixingMode !== FixingMode.VALORISATION;
  const isCategoryFees = form.watch("category") === "fees";

  useFeesCalculator(form);
  const isCategoryDisabled = !asset;

  useEffect(() => {
    if (!showQuantityField) {
      form.setValue("quantity", null, { shouldDirty: true });
    }
  }, [form, showQuantityField]);

  const prevPidRef = useRef<number | string | null>(null);
  useEffect(() => {
    const pid = portfolioId ?? null;
    const prev = prevPidRef.current;

    if (prev && !pid) {
      form.resetField("client", { defaultValue: null });
      form.resetField("bank_name", { defaultValue: null });
    }

    prevPidRef.current = pid;
  }, [portfolioId, form]);

  return (
    <div className="grid place-items-center bg-gray-50 py-2 min-h-full">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full max-w-card">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("transactions.new_transaction", {
                  defaultValue: "Nouvelle transaction",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t("transactions.transaction_date", {
                      defaultValue: "Date de la transaction",
                    })}
                    control={form.control}
                    isRequired
                  />
                </div>

                <AutocompleteAsyncField
                  name="c3_static_datum_name"
                  label={t("transactions.asset", {
                    defaultValue: "Actif",
                  })}
                  control={form.control}
                  base_url={`${Clients.C3}/${Tables.C3_STATIC_DATA}`}
                  id_column="c3_static_datum_id"
                  column_name="name"
                  isRequired
                  options={{
                    translateKey: "asset_class_2",
                    client: Clients.C3,
                    table_name: Tables.C3_STATIC_DATA,
                    language: uiLanguage,
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    key={`client-${portfolioId ?? "none"}`}
                    name="client"
                    label={t("transactions.client", {
                      defaultValue: "Client",
                    })}
                    control={form.control}
                    base_url={`${Clients.C3}/${Tables.C3_PORTFOLIOS}`}
                    id_column="c3_portfolio_id"
                    column_name="client"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: Boolean(portfolioId),
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (bankName) equals.bank_name = bankName;
                        if (portfolioId) equals.id = String(portfolioId);
                        if (supportedAccountType) {
                          equals.account_type = supportedAccountType;
                        }
                        return { equals };
                      },
                    }}
                  />
                  <AutocompleteAsyncField
                    key={`bank-${portfolioId ?? "none"}`}
                    name="bank_name"
                    label={t("transactions.bank", {
                      defaultValue: "Banque",
                    })}
                    control={form.control}
                    base_url={`${Clients.C3}/${Tables.C3_PORTFOLIOS}`}
                    id_column="c3_portfolio_id"
                    column_name="bank_name"
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: Boolean(portfolioId),
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (clientName) equals.client = clientName;
                        if (portfolioId) equals.id = String(portfolioId);
                        if (supportedAccountType) {
                          equals.account_type = supportedAccountType;
                        }
                        return { equals };
                      },
                    }}
                  />
                </div>

                <AutocompleteAsyncField
                  name="c3_portfolio_name"
                  label={t("transactions.account", {
                    defaultValue: "Compte",
                  })}
                  control={form.control}
                  base_url={`${Clients.C3}/${Tables.C3_PORTFOLIOS}`}
                  id_column="c3_portfolio_id"
                  column_name="name"
                  isRequired
                  options={{
                    filters: () => {
                      const equals: Record<string, string> = {};
                      if (clientName) equals.client = clientName;
                      if (bankName) equals.bank_name = bankName;
                      if (supportedAccountType) {
                        equals.account_type = supportedAccountType;
                      }
                      return { equals };
                    },
                  }}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_value_local_ccy"
                    label={t("transactions.amount_local", {
                      defaultValue: "Montant en devise locale",
                    })}
                    type="number"
                    control={form.control}
                    isRequired
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name="market_value_ptf_ccy"
                    label={t("transactions.amount_reporting", {
                      defaultValue: "Montant en devise de reporting",
                    })}
                    control={form.control}
                    type="number"
                    endContent={
                      <div className="opacity-50 text-xs">
                        {reportingCurrency}
                      </div>
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="quantity"
                    label={t("transactions.quantity", {
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                    type="number"
                    isRequired={!!asset && showQuantityField}
                    fieldOptions={{
                      isDisabled: isTypeCash || !showQuantityField,
                      changeOnDisable: false,
                    }}
                  />
                  <SelectField
                    name="transfer"
                    label={t("transactions.transfer_type", {
                      defaultValue: "Type de transfert",
                    })}
                    options={translations?.transfer}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldWithSection
                    label={t("transactions.movement_type", {
                      defaultValue: "Type de mouvement",
                    })}
                    name="category"
                    sections={categoryOptions.options as TSections}
                    control={form.control}
                    allow_empty_option={isCashAsset}
                    fieldOptions={{
                      isDisabled: isCategoryDisabled,
                      isVisible: !isCategoriesEmpty,
                    }}
                    isRequired={isTypeSecurities && !isCategoriesEmpty}
                    placeholder={t("form.placeholders.select")}
                  />
                </div>

                {isTypeCash && isCashAsset && (
                  <div className="mb-2">
                    <SelectField
                      name="fee_type"
                      label={t(`${Tables.C3_TRANSACTIONS}.columns.fee_type`, {
                        ns: "tables",
                        defaultValue: "Type de frais",
                      })}
                      control={form.control}
                      options={translations?.fee_type}
                      allow_empty_option
                      fieldOptions={{
                        isDisabled: !isCategoryFees,
                      }}
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="description"
                    label={t("transactions.movement_description", {
                      defaultValue: "Description du mouvement",
                    })}
                    control={form.control}
                  />
                </div>

                {isTypeSecurities && (
                  <div className="max-w-full bg-transparent text-xs">
                    <div className="py-2">
                      <Divider />
                      <div className="py-3">
                        <div className="mb-2">
                          <div className="text-xs">
                            {t("transactions.securities_transaction_fees", {
                              defaultValue: "Securities transaction fees",
                            })}
                          </div>
                        </div>

                        <div className="flex flex-col gap-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <AutocompleteLabelField
                              name="fee_currency"
                              label={t(`${Tables.C3_TRANSACTIONS}.columns.fee_currency`, {
                                ns: "tables",
                                defaultValue: "Fee currency",
                              })}
                              control={form.control}
                              options={currencyOptions}
                            />
                            <InputField
                              name="broker_fee"
                              label={t(`${Tables.C3_TRANSACTIONS}.columns.broker_fee`, {
                                ns: "tables",
                                defaultValue: "Broker fee",
                              })}
                              control={form.control}
                              type="number"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField
                              name="custodian_broker_fee"
                              label={t(
                                `${Tables.C3_TRANSACTIONS}.columns.custodian_broker_fee`,
                                {
                                  ns: "tables",
                                  defaultValue: "Custodian broker fee",
                                },
                              )}
                              control={form.control}
                              type="number"
                            />
                            <InputField
                              name="other_fee"
                              label={t(`${Tables.C3_TRANSACTIONS}.columns.other_fee`, {
                                ns: "tables",
                                defaultValue: "Other fee",
                              })}
                              control={form.control}
                              type="number"
                            />
                          </div>
                          <InputField
                            name="fees"
                            label={t(`${Tables.C3_TRANSACTIONS}.columns.fees`, {
                              ns: "tables",
                              defaultValue: "Total fees",
                            })}
                            control={form.control}
                            type="number"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>

              <CardFooter>
                <FormActionsNew onReset={handleReset} onDelete={openDeleteModal} />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
        >
          {changedFields
            ? NewDiff(
                changedFields,
                ["c3_static_datum_id", "c3_portfolio_id"],
                fieldTranslations,
              )
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
